import { useState } from 'react';
import { aiGeneratedJobs } from '../../../mocks/aiJobs';

export default function LiveJobFeed() {
  const [selectedTrade, setSelectedTrade] = useState('All');
  const [selectedUrgency, setSelectedUrgency] = useState('All');

  const trades = ['All', 'Carpentry', 'Electrical', 'Plumbing', 'Tiling', 'Roofing', 'HVAC', 'Painting'];
  const urgencies = ['All', 'Urgent', 'High', 'Medium', 'Low'];

  const filteredJobs = aiGeneratedJobs.filter(job => {
    const tradeMatch = selectedTrade === 'All' || job.tradeType === selectedTrade;
    const urgencyMatch = selectedUrgency === 'All' || job.urgency === selectedUrgency;
    return tradeMatch && urgencyMatch;
  });

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'Urgent':
        return 'bg-red-100 text-red-700';
      case 'High':
        return 'bg-orange-100 text-orange-700';
      case 'Medium':
        return 'bg-amber-100 text-amber-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-live-line"></i>
            <span>Live Job Feed</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Browse AI-Generated Jobs
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real jobs posted by verified clients, automatically structured by AI with scope and budget estimates
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Trade Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Filter by Trade</label>
              <div className="flex flex-wrap gap-2">
                {trades.map((trade) => (
                  <button
                    key={trade}
                    onClick={() => setSelectedTrade(trade)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedTrade === trade
                        ? 'bg-orange-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {trade}
                  </button>
                ))}
              </div>
            </div>

            {/* Urgency Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Filter by Urgency</label>
              <div className="flex flex-wrap gap-2">
                {urgencies.map((urgency) => (
                  <button
                    key={urgency}
                    onClick={() => setSelectedUrgency(urgency)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedUrgency === urgency
                        ? 'bg-orange-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {urgency}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Job Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-6 border-2 border-transparent hover:border-orange-200"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{job.title}</h3>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      <span>{job.postedTime}</span>
                    </div>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${getUrgencyColor(job.urgency)}`}>
                  {job.urgency}
                </div>
              </div>

              {/* Trade Type Badge */}
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <i className="ri-tools-line"></i>
                <span>{job.tradeType}</span>
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-4 leading-relaxed">{job.description}</p>

              {/* AI-Generated Scope */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
                  <i className="ri-robot-2-line text-orange-600"></i>
                  <span>AI-Generated Scope</span>
                </div>
                <ul className="space-y-1">
                  {job.scope.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Budget & Client Info */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">AI Estimated Budget</div>
                  <div className="text-2xl font-bold text-orange-600">{job.aiEstimatedBudget}</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
                    <i className="ri-star-fill text-amber-500"></i>
                    <span>{job.clientRating}</span>
                    {job.verified && <i className="ri-verified-badge-fill text-blue-600"></i>}
                  </div>
                  <div className="text-xs text-gray-500">{job.jobsPosted} jobs posted</div>
                </div>
              </div>

              {/* CTA Button */}
              <button className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                <span>Apply for This Job</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          ))}
        </div>

        {/* Load More */}
        <div className="text-center">
          <button className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all border-2 border-gray-200 whitespace-nowrap cursor-pointer">
            <span>Load More Jobs</span>
            <i className="ri-refresh-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}